import { useTable } from '@refinedev/antd';
import { useCustomMutation } from '@refinedev/core';
import { Table, Tag, Progress, Modal, InputNumber, Input, Button } from 'antd';
import { ShieldCheck } from 'lucide-react';
import { useState, type FC } from 'react';

interface QuotaRecord {
  id: string;
  organizationName: string;
  plan: string;
  currentUsage: number;
  effectiveLimit: number;
  byokEnabled: boolean;
}

const PLAN_TAG: Record<string, { color: string; text: string }> = {
  free: { color: 'default', text: 'Free' },
  pro: { color: 'blue', text: 'Pro' },
  team: { color: 'purple', text: 'Team' },
  business: { color: 'gold', text: 'Business' },
  enterprise: { color: 'green', text: 'Enterprise' },
};

const getGaugeColor = (pct: number) => {
  if (pct > 80) return '#ef4444';
  if (pct > 60) return '#f59e0b';
  return '#10b981';
};

const QuotaTable: FC = () => {
  const { tableProps } = useTable<QuotaRecord>({
    resource: 'admin/subscription/quotas',
    pagination: { pageSize: 10 },
  });

  const { mutate: overrideQuota, isLoading: overriding } = useCustomMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<QuotaRecord | null>(null);
  const [newLimit, setNewLimit] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  const openOverride = (record: QuotaRecord) => {
    setSelectedOrg(record);
    setNewLimit(record.effectiveLimit);
    setReason('');
    setModalOpen(true);
  };

  const submitOverride = () => {
    if (!selectedOrg || !newLimit) return;
    overrideQuota(
      {
        url: `/api/v1/admin/subscription/organizations/${selectedOrg.id}/quota/override`,
        method: 'post',
        values: { newLimit, reason },
      },
      { onSuccess: () => setModalOpen(false) },
    );
  };

  return (
    <div className="fc-card overflow-hidden p-0">
      <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
        <ShieldCheck className="h-4 w-4 text-slate-500 dark:text-slate-400" />
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
          Quotas par Organisation
        </h3>
      </div>

      <Table {...tableProps} rowKey="id" size="middle">
        <Table.Column dataIndex="organizationName" title="Organisation" />
        <Table.Column
          dataIndex="plan"
          title="Plan"
          render={(plan: string) => {
            const cfg = PLAN_TAG[plan] ?? { color: 'default', text: plan };
            return <Tag color={cfg.color}>{cfg.text}</Tag>;
          }}
        />
        <Table.Column
          title="Usage"
          render={(_: unknown, r: QuotaRecord) =>
            r.byokEnabled
              ? '—'
              : `${r.currentUsage.toLocaleString('fr-FR')} / ${r.effectiveLimit.toLocaleString('fr-FR')}`
          }
        />
        <Table.Column
          title="Jauge"
          width={160}
          render={(_: unknown, r: QuotaRecord) => {
            if (r.byokEnabled) return <Tag color="blue">BYOK</Tag>;
            const pct = Math.round((r.currentUsage / (r.effectiveLimit || 1)) * 100);
            return <Progress percent={pct} size="small" strokeColor={getGaugeColor(pct)} />;
          }}
        />
        <Table.Column
          title="Actions"
          width={110}
          render={(_: unknown, r: QuotaRecord) =>
            r.byokEnabled ? null : (
              <Button size="small" onClick={() => openOverride(r)}>
                Override
              </Button>
            )
          }
        />
      </Table>

      <Modal
        title="Override Quota"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submitOverride}
        confirmLoading={overriding}
        okText="Appliquer"
        cancelText="Annuler"
      >
        <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">
          Organisation : <strong>{selectedOrg?.organizationName}</strong>
        </p>
        <div className="mb-3">
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            Nouveau quota (tokens)
          </label>
          <InputNumber
            className="w-full"
            min={0}
            value={newLimit}
            onChange={(v) => setNewLimit(v)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-700 dark:text-slate-300">
            Raison
          </label>
          <Input.TextArea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Raison de l'override..."
          />
        </div>
      </Modal>
    </div>
  );
};

export default QuotaTable;
